import type { FileCollectionTable } from "@msuite/utils/types";
import type { AppSubjectName } from "./strategies/resource-map";

/**
 * The subject whose `update` rule decides who may attach to a parent, per file collection — and
 * with it who may replace, unlink, move, annotate or remove what hangs there.
 *
 * File is deliberately not a subject. A file carries no rights of its own: it is a property of
 * the ticket, the article or the project it is attached to, and the person who may edit that
 * record may change its attachments. One map therefore says which subject a collection answers
 * to, and both sides of the wire read it — the server before it writes a link, a client before
 * it renders an attach button, because a control whose request is a 403 is worse than no control.
 *
 * `"Ticket"` is the one value that is not a manifest subject: ticket work is a relationship to
 * the project rather than a grant (`Citadel.canEditTickets`), and a ticket's files follow that
 * relationship exactly as the ticket itself does.
 *
 * Reading is not here, because reading a file asks no rule: every member of the organisation
 * reads the organisation's files — the worksite risk assessments are attachments and every
 * employee opens one to clock in — and the claims-carrying connection is the whole boundary.
 *
 * The server's registry (`apps/server/src/handlers/files/registry.ts`) is where each choice is
 * argued entry by entry, next to the statement that loads the parent in the shape its rules bind
 * on, and `handlers/files/files.test.ts` compares the two so they cannot drift.
 */
export const FILE_PARENT_SUBJECTS = {
	articleFiles: "Stock",
	outgoingInvoiceFiles: "Invoice",
	noteFiles: "Note",
	organizationServiceFiles: "Service",
	partnerReportFiles: "Partner",
	projectDocumentFiles: "Project",
	projectFiles: "Project",
	protocolFiles: "Protocol",
	returnOrderFiles: "Stock",
	serviceCalculationFiles: "Calculation",
	serviceCalculationItemFiles: "Calculation",
	suborderFiles: "Stock",
	ticketFiles: "Ticket",
	vehicleDocumentFiles: "Vehicle",
	wikiPageFiles: "Wiki",
} as const satisfies Record<FileCollectionTable, Exclude<AppSubjectName, "all"> | "Ticket">;

/** What a file collection's parent answers to: a manifest subject, or the fixed ticket rule. */
export type FileParentSubject = (typeof FILE_PARENT_SUBJECTS)[FileCollectionTable];
